import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useTrips } from "@/api/trips";
import { Trip } from "@/api.types";
import { CreateTripButton } from "./create-trip-button";
import { ViewTripButton } from "./view-trip-button";

const statusColors = {
  PLANNED: "bg-blue-500",
  ACTIVE: "bg-yellow-500",
  COMPLETED: "bg-green-500",
  ARCHIVED: "bg-gray-500",
};

const TRIP_STATUSES = {
  PLANNED: "Planned",
  ACTIVE: "Active",
  COMPLETED: "Completed",
  ARCHIVED: "Archived",
} as const;

export function TripsTable() {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [statusFilter, setStatusFilter] = useState<Trip["status"] | null>(null);

  const { data: tripsResponse } = useTrips({
    page,
    limit,
    status: statusFilter ?? undefined,
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end mb-4 gap-4">
        <CreateTripButton />
        {/* Status Filter */}
        <div className="flex justify-end mb-4">
          <Select
            value={statusFilter ?? "ALL"}
            onValueChange={(value) =>
              setStatusFilter(
                value === "ALL" ? null : (value as Trip["status"])
              )
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Statuses</SelectItem>
              {Object.entries(TRIP_STATUSES).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Trip Number</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Time Spent</TableHead>
              <TableHead>Operator</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Depot</TableHead>
              <TableHead>Tasks</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tripsResponse?.data.map((trip) => (
              <TableRow key={trip.id}>
                <TableCell>
                  <div>
                    <p className="text-md font-medium">#{trip.tripNumber}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    className={`${statusColors[trip.status]}`}
                    variant="default"
                  >
                    {trip.status.replace(/_/g, " ")}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div>
                    <div>
                      {trip.startTime && (
                        <p className="text-sm text-gray-500">
                          Started: {new Date(trip.startTime).toLocaleString()}
                        </p>
                      )}
                      {trip.endTime && (
                        <p className="text-sm text-gray-500">
                          Ended: {new Date(trip.endTime).toLocaleString()}
                        </p>
                      )}
                      {trip.actualDuration !== null && (
                        <p className="font-medium">
                          Duration: {trip.actualDuration} mins
                        </p>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">
                      {trip.operator?.user
                        ? `${trip.operator.user.firstName} ${trip.operator.user.lastName}`
                        : "No operator assigned"}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">
                      {trip.vehicle?.plateNumber ?? "No vehicle assigned"}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">
                      {trip.depot?.name ?? "No depot assigned"}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{trip.tasks.length} tasks</p>
                    <p className="text-sm text-gray-500">
                      {trip.waypoints.length} stops
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <ViewTripButton tripId={trip.id} size="sm" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-gray-500">
          Showing {(page - 1) * limit + 1} to{" "}
          {Math.min(page * limit, tripsResponse?.metadata.total ?? 0)} of{" "}
          {tripsResponse?.metadata.total ?? 0} results
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= (tripsResponse?.metadata.totalPages ?? 0)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
